// ABOUTME: Drops point-tag candidates whose value says nothing a sibling tag isn't already saying.
// ABOUTME: No React, no DOM, no brand vocabulary: pure token-subset comparison over resolved copy.

// Three tags reached one hold on the dial chapter reading "Geometric grid with raised polished
// steel bridges" / "Raised Polished Bridges" / "Polished Steel" (owner report, 2026-08-27). Every
// one was legitimately matched — the agent said a sentence that mentions all three keys — and the
// catalogue's own copy is what overlapped. The catalogue has since been trimmed, but the shape of
// the failure is not specific to those fields or to this tenant: any two attributes whose authored
// values restate each other will do it again. So the guard lives here, in the tag pipeline, rather
// than being a one-off correction to one brand's data.
//
// Three rules, from strict to heuristic (the first shipped 2026-08-27, the others the same
// evening after the exploded/micro-rotor screenshot showed "Schwarz Etienne ASE 200 Micro-rotor" /
// "Time Only, Micro-rotor Automatic" / "Integrated Micro-rotor" all on one hold — no pair is a
// subset, and the guest still reads the same word three times):
//   1. SUBSET — every meaningful word already visible in one tag → dropped.
//   2. NEAR-DUPLICATE — the candidate shares ≥2 words with one visible tag and adds only ONE new
//      word to it ("Raised Polished Steel Bridges" over "…raised polished bridges…") → dropped.
//   3. ECHO CAP — a distinctive word (≥4 letters or hyphenated; never a bare number) already
//      visible in TWO tags admits no third repetition. The first two mentions are legitimate
//      context + detail; the third is the point where the hold reads as one idea photocopied.
//      This rule runs in MATCH order, not longest-first: the agent's speech order is the only
//      signal of which repetition the guest actually asked about ("Integrated Micro-rotor"
//      matched on the micro-rotor question must beat "Time Only, Micro-rotor Automatic"
//      arriving later, regardless of which value is wordier).
// Overlap below those bars ("Rhodium-plated Polished Steel" against "Raised Polished Bridges",
// two new words) is a genuine distinction and is left alone — guessing harder at near-duplicates
// would start hiding real information.

// Joining words only. NOT domain vocabulary: nothing here names a material, a finish or a part
// (the D22 grep-gate that resolveTagContent.js and SpecSheetOverlay.js also answer to).
const JOINERS = new Set(['with', 'and', 'the', 'a', 'an', 'of', 'in', 'on', 'or']);

/** Meaningful lowercase words of a tag value, as a Set. Punctuation and joiners fall out. */
function tokenize(value) {
  return new Set(
    String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter((word) => word && !JOINERS.has(word)),
  );
}

const isSubsetOf = (small, big) => small.size > 0 && [...small].every((w) => big.has(w));

// Rule 2: candidate says what `other` says plus at most one new word (and genuinely overlaps it).
const isNearDuplicateOf = (candidate, other) => {
  const fresh = [...candidate].filter((w) => !other.has(w));
  return candidate.size - fresh.length >= 2 && fresh.length <= 1;
};

// Rule 3's notion of a word worth counting echoes of: long enough (or compound enough) to be the
// word the guest actually notices repeating, and not a bare number like "200".
const isDistinctive = (word) => !/^\d+$/.test(word) && (word.length >= 4 || word.includes('-'));

/**
 * @param {object} params
 * @param {string[]} params.candidates - attribute keys about to be admitted, in match order.
 * @param {string[]} [params.live] - keys already on screen; a candidate they cover is dropped.
 * @param {(key: string) => ({ value: string }|null)} params.contentFor - the same resolver the
 *   layer renders with, so this compares the words the guest would actually read rather than the
 *   raw catalogue field (curated `tag_copy` wins there, and would otherwise be compared as specs).
 * @returns {string[]} the candidates worth showing, in the order given.
 */
export function suppressRedundantTags({ candidates, live = [], contentFor }) {
  const tokensFor = (key) => tokenize(contentFor(key)?.value);

  // Longest first, so the most informative wording is the one that survives a subset pair: the
  // guest reads the sentence that contains the others, not the fragment that contains nothing.
  const ranked = candidates
    .map((key) => ({ key, tokens: tokensFor(key) }))
    .sort((a, b) => b.tokens.size - a.tokens.size);

  const kept = live.map((key) => ({ key, tokens: tokensFor(key) })).filter((t) => t.tokens.size);

  const survivors = new Set();
  for (const candidate of ranked) {
    // An empty/unresolvable value carries no words to compare — leave it to the layer's own
    // content filter to drop, rather than silently swallowing it here.
    if (!candidate.tokens.size) { survivors.add(candidate.key); continue; }
    if (kept.some((other) => isSubsetOf(candidate.tokens, other.tokens)
      || isNearDuplicateOf(candidate.tokens, other.tokens))) continue;
    kept.push(candidate);
    survivors.add(candidate.key);
  }

  // Rule 3, in match order over what rules 1–2 let through (live tags count as already visible).
  const echoCounts = new Map();
  const countTokens = (tokens) => tokens.forEach((w) => {
    if (isDistinctive(w)) echoCounts.set(w, (echoCounts.get(w) ?? 0) + 1);
  });
  live.forEach((key) => countTokens(tokensFor(key)));
  const result = candidates.filter((key) => {
    if (!survivors.has(key)) return false;
    const tokens = tokensFor(key);
    if (tokens.size && [...tokens].some((w) => (echoCounts.get(w) ?? 0) >= 2)) return false;
    countTokens(tokens);
    return true;
  });

  // Match order throughout: admission order is what staggers the reveal, and reordering it would
  // change which tag lands in which authored anchor slot.
  return result;
}

export default suppressRedundantTags;
